import { RESOURCES } from './resources/index.js';

/**
 * Permission keys derived from the catalog — the SINGLE source of truth.
 *
 * For every catalog resource, exposes `{ <action>: "<resourceKey>:<action>" }`
 * for exactly the actions that resource supports, fully typed from the `as const`
 * catalog. Both the backend's route guards and the frontend's capability checks
 * reference these instead of re-declaring per-call-site string literals:
 *
 *   // backend — via the `@src/lib/iam/permissions.js` re-export shim
 *   hasPermission(IAM_PERMISSIONS['api-key'].create)
 *
 *   // frontend
 *   import { IAM_PERMISSIONS } from '@retracedgmbh/modules/permissions/contracts';
 *   can(IAM_PERMISSIONS['permission-access'].read)
 *
 * Adding/removing a resource action in `resources/` flows here automatically; a
 * caller referencing an action its resource does not support fails to compile.
 */
type Resource = (typeof RESOURCES)[number];

type PermissionsFor<R extends Resource> = {
  [A in R['actions'][number]]: `${R['key']}:${A}`;
};

export type IamPermissions = {
  [R in Resource as R['key']]: PermissionsFor<R>;
};

export const IAM_PERMISSIONS: IamPermissions = Object.fromEntries(
  RESOURCES.map(r => [
    r.key,
    Object.fromEntries(r.actions.map(a => [a, `${r.key}:${a}`])),
  ]),
) as IamPermissions;
